import { getOptionCheckTypes } from "../../order-utils";
import { CheckData } from "../../types";

// FP-1018: Top-level cards hidden from the CP quickscreen tab when the
// `client-portal--hide-ccrc-and-canadian-credit-for-quickscreen` flag is ON
// (LaunchDarkly camelCases the slug to
// `clientPortalHideCcrcAndCanadianCreditForQuickscreen` via the `useFlags()`
// hook).
// CRIMINAL_RECORD_REPORT_1 is hidden in full because on quickscreen it only
// surfaces CCRC children. CANADIAN_CREDIT_REPORT_1 and
// CANADIAN_SHORT_CONSUMER_CREDIT_1 are hidden as standalone cards (they
// appear standalone when `enableAddressBasedCreditCheckOrdering` is OFF, or
// when surfaced outside the CREDIT_REPORT_1 umbrella).
const HIDDEN_TOP_LEVEL_CHECK_TYPES_ON_QUICKSCREEN = new Set<string>([
  "CRIMINAL_RECORD_REPORT_1",
  "CANADIAN_CREDIT_REPORT_1",
  "CANADIAN_SHORT_CONSUMER_CREDIT_1",
]);

// FP-1018: Child / select-option values stripped from the CREDIT_REPORT_1
// umbrella card so a user with the flag ON cannot pick Canada inside the
// address-based credit selector. CANADIAN_SHORT_CONSUMER_CREDIT_1 is not a
// child of CREDIT_REPORT_1 today, but is included defensively in case it is
// added later.
const HIDDEN_CANADIAN_CREDIT_CHILD_TYPES = new Set<string>([
  "CANADIAN_CREDIT_REPORT_1",
  "CANADIAN_SHORT_CONSUMER_CREDIT_1",
]);

/**
 * FP-1018: Returns true if a select-option resolves to (or requires) any of
 * the hidden Canadian credit check types. Robust to two payload shapes the
 * backend uses today:
 *
 *   1. `value` is the check type directly (string or string[]) — handled by
 *      the shared `getOptionCheckTypes` helper from `order-utils.tsx`.
 *   2. `value` is a country code like `"CA"` and `required_check_types`
 *      carries the actual check types (see `order-utils.test.tsx` fixtures).
 *
 * Either shape is enough to identify a Canadian-credit option, so we OR
 * across both fields.
 */
function optionTargetsHiddenCanadianCredit(
  option: CheckData["select_options"][number]
): boolean {
  const valueTypes = getOptionCheckTypes(option);
  if (valueTypes.some((t) => HIDDEN_CANADIAN_CREDIT_CHILD_TYPES.has(t))) {
    return true;
  }
  const requiredTypes = option.required_check_types ?? [];
  return requiredTypes.some((t) => HIDDEN_CANADIAN_CREDIT_CHILD_TYPES.has(t));
}

/**
 * FP-1018: Remove Canadian credit children / select-options from an umbrella
 * card so they cannot be picked when the hide flag is on. Returns:
 *
 * - the input untouched if there is nothing to strip (identity-stable so
 *   React `useMemo` / list diffs don't see spurious churn);
 * - a new `CheckData` with cleaned `children` / `select_options`;
 * - `null` if stripping leaves the umbrella with no selectable children AND
 *   no selectable options, so the caller can drop the empty umbrella card
 *   instead of rendering a broken one.
 */
function stripCanadianCreditChildren(check: CheckData): CheckData | null {
  const children = check.children ?? [];
  const selectOptions = check.select_options ?? [];

  const filteredChildren = children.filter(
    (child) => !HIDDEN_CANADIAN_CREDIT_CHILD_TYPES.has(child.type)
  );
  const filteredSelectOptions = selectOptions.filter(
    (option) => !optionTargetsHiddenCanadianCredit(option)
  );

  const childrenChanged = filteredChildren.length !== children.length;
  const optionsChanged = filteredSelectOptions.length !== selectOptions.length;
  if (!childrenChanged && !optionsChanged) {
    return check;
  }

  // If the umbrella started with children/options but now has none of either,
  // there's nothing left to select — drop the card instead of rendering it
  // empty and broken. We only drop when the *original* card had something to
  // pick, so unrelated cards that legitimately have no children/options stay
  // visible (they hit the `!changed` early return above anyway).
  if (filteredChildren.length === 0 && filteredSelectOptions.length === 0) {
    return null;
  }

  return {
    ...check,
    children: filteredChildren,
    select_options: filteredSelectOptions,
  };
}

/**
 * FP-1018: Apply the hide-CCRC-and-Canadian-credit filter to the
 * available-checks response when the flag is on and we are on the
 * quickscreen tab. UI-only filter — backend availability is unchanged.
 *
 * Exported for unit testing and for `useCheckSectionData` to memoize over.
 */
export function applyHideCcrcAndCanadianCreditQuickscreenFilter(
  checks: CheckData[]
): CheckData[] {
  const result: CheckData[] = [];
  for (const check of checks) {
    if (HIDDEN_TOP_LEVEL_CHECK_TYPES_ON_QUICKSCREEN.has(check.type)) {
      continue;
    }
    const stripped = stripCanadianCreditChildren(check);
    if (stripped === null) continue;
    result.push(stripped);
  }
  return result;
}
